// examinerBank.js — bulk-authored examiner-brain blocks indexed by topic id.
//
// We attach these via a runtime decorator inside scienceContent.js / mathsContent.js / englishContent.js
// rather than editing every individual GCSE data file.
//
// Adding a new entry here instantly lights up the "Examiner brain" tab on the matching topic.
//
// Supported keys:
// - GCSE Science topic ids, e.g. "gcse-b1-7"
// - GCSE Maths topic ids, e.g. "2.4"
// - Maths problem ids, e.g. "F-001"
// - GCSE English topic ids, e.g. "eng-8-1"
// - Paper-level ids, e.g. "paper1-language"

export const EXAMINER_BANK = {
  // ============================================================
  // BIOLOGY GCSE
  // ============================================================

  'gcse-b1-7': {
    whyMarksLost: [
      'Confusing diffusion (concentration gradient) with osmosis (water specifically).',
      'Forgetting "active transport" needs ENERGY from respiration — examiners often want the word "ATP" or "respiration".',
      'Not specifying "partially permeable" when describing osmosis.',
    ],
    whatExaminersWant: [
      'Precise definitions, especially "net movement".',
      'Osmosis described as water moving through a partially permeable membrane.',
      'Active transport linked explicitly to energy released by respiration.',
      'A real-world example such as root hair cells or glucose absorption in the small intestine when asked to apply knowledge.',
    ],
    commonTraps: [
      'Saying particles "want" to move down a gradient. Avoid teleology; use "net movement".',
      'Forgetting that diffusion, osmosis and active transport can all occur in living cells.',
      'Writing a vague answer such as "things move in and out of cells" without naming the process.',
    ],
    gradeNineMove:
      'Always say "net movement", not just "movement". That one word often separates a vague answer from a mark-worthy scientific definition.',
    modelSentence:
      'Osmosis is the net movement of water molecules from a dilute solution to a more concentrated solution through a partially permeable membrane.',
  },

  'gcse-b3-5': {
    whyMarksLost: [
      'Saying vaccines "give immunity" without explaining how memory cells are produced.',
      'Confusing antibody with antigen — antibodies are proteins made by lymphocytes, antigens are markers on pathogens.',
      'Failing to mention that the secondary immune response is faster and stronger.',
    ],
    whatExaminersWant: [
      'A clear chain: vaccine → antigen exposure → lymphocytes → antibodies → memory cells → faster response on real infection.',
      'The phrase "specific antibodies" used correctly.',
      'Understanding that vaccination reduces spread through herd immunity when enough people are immune.',
    ],
    commonTraps: [
      'Saying antibodies destroy pathogens directly. More accurately, they bind to antigens and help target pathogens for destruction.',
      'Forgetting antibiotics do not work against viruses.',
      'Writing that vaccines contain "the disease" without clarifying weakened, inactive, harmless or antigenic material.',
    ],
    gradeNineMove:
      'Use "complementary" when describing antibody-antigen binding. It mirrors lock-and-key language and signals secure biological understanding.',
    modelSentence:
      'The vaccine exposes the body to harmless antigens, causing lymphocytes to produce specific antibodies and memory cells so the real pathogen can be destroyed more rapidly in future.',
  },

  'gcse-b5-2': {
    whyMarksLost: [
      'Mixing up the order of neurones in a reflex arc.',
      'Forgetting synapse → neurotransmitter → next neurone.',
      'Saying the brain controls reflex actions directly rather than recognising the spinal cord pathway.',
    ],
    whatExaminersWant: [
      'A clear chain: stimulus → receptor → sensory neurone → relay neurone → motor neurone → effector → response.',
      'Mention of synapses and chemical neurotransmitters.',
      'Recognition that reflex actions are rapid, automatic and protective.',
    ],
    commonTraps: [
      'Calling effectors "muscles only" — effectors can be muscles or glands.',
      'Drawing arrows the wrong way on diagrams.',
      'Forgetting that receptors detect stimuli.',
    ],
    gradeNineMove:
      'For six-mark reflex-arc questions, number each stage in the pathway. Examiners can then award each mark without hunting through the paragraph.',
    modelSentence:
      'The receptor detects the stimulus and sends an impulse along a sensory neurone to a relay neurone in the spinal cord, which passes the impulse to a motor neurone so the effector produces a rapid response.',
  },

  'gcse-b6-3': {
    whyMarksLost: [
      'Confusing genotype with phenotype.',
      'Forgetting that BB and Bb both show the dominant phenotype.',
      'Not using a Punnett square when the question asks for one.',
    ],
    whatExaminersWant: [
      'A complete Punnett square with parent gametes labelled at the edges.',
      'Both genotype ratio and phenotype ratio when required.',
      'Probabilities expressed in the format requested by the question: fraction, percentage or ratio.',
    ],
    commonTraps: [
      'Using single letters that look identical for dominant and recessive alleles. Use B and b, not two capital letters.',
      'Saying "every child will have this chance" without explaining that each pregnancy is independent.',
      'Forgetting that carriers can have a recessive allele without showing the recessive phenotype.',
    ],
    gradeNineMove:
      'State your assumption clearly, for example: "assuming brown eyes (B) is fully dominant over blue eyes (b)". This gives precision and protects against ambiguous genetics questions.',
    modelSentence:
      'If B is dominant and b is recessive, a Bb individual will have the dominant phenotype but still carries the recessive allele.',
  },

  'gcse-b6-6': {
    whyMarksLost: [
      'Saying species "evolve to" survive, which wrongly implies intention.',
      'Conflating mutation with natural selection.',
      'Forgetting that useful variation must be heritable to affect future generations.',
    ],
    whatExaminersWant: [
      'A five-step chain: variation → selection pressure → differential survival → reproduction → allele frequency change.',
      'A named example such as antibiotic resistance or the peppered moth when asked to apply.',
      'Recognition that populations evolve over generations, not individuals during their lifetime.',
    ],
    commonTraps: [
      'Saying organisms adapt during their lifetime and pass that acquired feature on.',
      'Confusing antibiotic resistance with antibody resistance.',
      'Forgetting that mutations are random but selection is non-random.',
    ],
    gradeNineMove:
      'When explaining antibiotic resistance, use the word "selected". It shows the mechanism without making bacteria sound as if they consciously choose to survive.',
    modelSentence:
      'Random mutations create variation; if a mutation gives an organism a survival advantage, that organism is more likely to reproduce and pass the allele on, so the allele becomes more common over generations.',
  },

  // ============================================================
  // CHEMISTRY GCSE
  // ============================================================

  'gcse-c1-7': {
    whyMarksLost: [
      'Saying alkali metals are reactive because they are soft. Softness is a property, not the reason for reactivity.',
      'Forgetting the link: outer electron further from nucleus → easier to lose → more reactive.',
      'Not balancing equations, for example: 2Na + 2H₂O → 2NaOH + H₂.',
    ],
    whatExaminersWant: [
      'Reasoning that goes beyond "more shells". Mention shielding and distance from the nucleus.',
      'Balanced symbol equations where required.',
      'Correct comparison between Group 1 and Group 7 trends.',
    ],
    commonTraps: [
      'Confusing Group 1 trend, where reactivity increases down the group, with Group 7, where reactivity decreases down the group.',
      'Saying "loses electrons easily" without explaining why.',
      'Forgetting that Group 1 metals form 1+ ions.',
    ],
    gradeNineMove:
      'Use the phrase "the outer electron is more shielded by inner shells and further from the nucleus". Both mechanisms are usually mark-worthy.',
    modelSentence:
      'Reactivity increases down Group 1 because the outer electron is further from the nucleus and more shielded, so it is lost more easily.',
  },

  'gcse-c2-3': {
    whyMarksLost: [
      'Saying metals conduct because of "electrons" without specifying delocalised electrons.',
      'Confusing malleability with ductility.',
      'Forgetting that alloys are harder because different-sized atoms disrupt the layers.',
    ],
    whatExaminersWant: [
      'The phrase "delocalised electrons" used accurately.',
      'The layers-and-sliding explanation for malleability and ductility.',
      'The alloy explanation: different-sized atoms stop layers sliding easily.',
    ],
    commonTraps: [
      'Calling metallic bonding covalent or ionic.',
      'Saying alloys always melt at higher temperatures.',
      'Forgetting that metals conduct as solids and liquids because delocalised electrons can move.',
    ],
    gradeNineMove:
      'Describe metallic bonding as "electrostatic attraction between positive metal ions and a sea of delocalised electrons". That phrasing is extremely mark-friendly.',
    modelSentence:
      'Metals conduct electricity because delocalised electrons can move through the structure and carry charge.',
  },

  'gcse-c3-2': {
    whyMarksLost: [
      'Forgetting to calculate Mᵣ before using moles = mass ÷ Mᵣ.',
      'Counting atoms incorrectly in formulae with brackets or multiple ions.',
      'Mixing up Aᵣ, which is for an atom, with Mᵣ, which is for a compound or molecule.',
    ],
    whatExaminersWant: [
      'A line showing Mᵣ as the sum of atomic masses.',
      'The formula moles = mass ÷ Mᵣ written before substitution.',
      'Correct handling of brackets, subscripts and diatomic elements.',
    ],
    commonTraps: [
      'Forgetting O₂ has Mᵣ 32, not 16.',
      'Miscounting Ca(OH)₂ as one oxygen and one hydrogen instead of two of each.',
      'Rounding too early and losing accuracy.',
    ],
    gradeNineMove:
      'Write the Mᵣ calculation as a separate equation before calculating moles. Two visible steps create two markable lines.',
    modelSentence:
      'Mᵣ of Ca(OH)₂ = 40 + (16 + 1) × 2 = 74, so moles = mass ÷ 74.',
  },

  'gcse-c4-2': {
    whyMarksLost: [
      'Confusing acid strength with concentration.',
      'Forgetting that pH is logarithmic.',
      'Not knowing that acids release H⁺ ions in solution and alkalis release OH⁻ ions.',
    ],
    whatExaminersWant: [
      'A definition of an acid as a substance that releases H⁺ ions in aqueous solution.',
      'A clear distinction between strong/weak and concentrated/dilute.',
      'Recognition that each pH unit represents a tenfold change in hydrogen ion concentration.',
    ],
    commonTraps: [
      'Saying "strong acid means concentrated acid".',
      'Calling weak acids harmless.',
      'Forgetting that a weak acid only partially dissociates in solution.',
    ],
    gradeNineMove:
      'When comparing acidity, say "a change of one pH unit is a tenfold change in H⁺ concentration".',
    modelSentence:
      'A strong acid fully dissociates in water, whereas a weak acid only partially dissociates; concentration is a separate measure of how much acid is present per unit volume.',
  },

  'gcse-c4-4': {
    whyMarksLost: [
      'Confusing cathode and anode.',
      'Forgetting aluminium oxide must be molten so ions can move.',
      'Writing half-equations without electrons.',
    ],
    whatExaminersWant: [
      'Correct half-equations: Al³⁺ + 3e⁻ → Al and 2O²⁻ → O₂ + 4e⁻.',
      'Recognition that reduction occurs at the cathode and oxidation occurs at the anode.',
      'Explanation that cryolite lowers the melting point, reducing energy cost.',
    ],
    commonTraps: [
      'Saying "the metal is more reactive than carbon" without explaining why electrolysis is required.',
      'Forgetting that oxygen forms at the positive electrode.',
      'Not balancing charges in half-equations.',
    ],
    gradeNineMove:
      'On extraction questions, state the trade-off: electrolysis is energy-expensive but necessary for metals more reactive than carbon.',
    modelSentence:
      'Aluminium is extracted by electrolysis because aluminium is more reactive than carbon, so carbon cannot displace it from aluminium oxide.',
  },

  // ============================================================
  // PHYSICS GCSE
  // ============================================================

  'gcse-p1-3': {
    whyMarksLost: [
      'Forgetting to convert mass to kilograms.',
      'Mixing up specific heat capacity with specific latent heat.',
      'Ignoring heat loss as a source of error in the practical.',
    ],
    whatExaminersWant: [
      'The formula ΔE = m c Δθ written clearly.',
      'A line showing Δθ = final temperature − initial temperature.',
      'Mention of heat loss to surroundings when evaluating the method.',
    ],
    commonTraps: [
      'Using J/kg instead of J/kg°C for specific heat capacity.',
      'Using total temperature instead of temperature change.',
      'Rounding too early in calculations.',
    ],
    gradeNineMove:
      'When asked about errors, state the direction: heat loss makes the calculated specific heat capacity too high because more energy was supplied than actually heated the material.',
    modelSentence:
      'Specific heat capacity is the energy needed to raise the temperature of 1 kg of a substance by 1°C.',
  },

  'gcse-p2-1': {
    whyMarksLost: [
      'Mixing up series and parallel circuit rules.',
      'Calculating parallel resistance by simply adding resistors.',
      'Forgetting that potential difference is shared in series but the same across branches in parallel.',
    ],
    whatExaminersWant: [
      'V = IR rearranged correctly.',
      'Correct series and parallel rules.',
      'Recognition that total resistance in parallel is less than the smallest individual branch resistance.',
    ],
    commonTraps: [
      'Confusing potential difference with current.',
      'Forgetting an ideal ammeter has negligible resistance and is placed in series.',
      'Forgetting an ideal voltmeter has very high resistance and is placed in parallel.',
    ],
    gradeNineMove:
      'Sketch the circuit even if the question gives one in words. Visual reasoning prevents series/parallel mistakes.',
    modelSentence:
      'In a series circuit the current is the same everywhere, while in a parallel circuit the potential difference across each branch is the same.',
  },

  'gcse-p4-2': {
    whyMarksLost: [
      'Mixing up alpha and beta properties.',
      'Forgetting to balance mass and atomic numbers in nuclear equations.',
      'Calling gamma a particle rather than electromagnetic radiation.',
    ],
    whatExaminersWant: [
      'Balanced nuclear equations where total mass and atomic numbers match on both sides.',
      'Correct penetration order: alpha stopped by paper, beta by thin aluminium, gamma reduced by thick lead or concrete.',
      'Correct ionising order: alpha > beta > gamma.',
    ],
    commonTraps: [
      'Confusing penetration with ionising power.',
      'Forgetting beta-minus emission turns a neutron into a proton.',
      'Saying radiation "kills all cells" without recognising ionisation and damage to DNA.',
    ],
    gradeNineMove:
      'Write nuclear equations with mass numbers and atomic numbers clearly, then check both totals before moving on.',
    modelSentence:
      'Alpha radiation is the most ionising but least penetrating, whereas gamma radiation is the least ionising but most penetrating.',
  },

  'gcse-p4-3': {
    whyMarksLost: [
      'Treating half-life as a linear decrease rather than repeated halving.',
      'Confusing half-life with activity.',
      'Failing to keep time units consistent.',
    ],
    whatExaminersWant: [
      'Use of activity = original activity × (1/2)ⁿ.',
      'Step-by-step halving chains where appropriate.',
      'Correct reading of half-life from decay graphs.',
    ],
    commonTraps: [
      'Saying the substance disappears after one half-life.',
      'Forgetting background radiation may need to be subtracted.',
      'Reading graph scales inaccurately.',
    ],
    gradeNineMove:
      'On graph questions, read the time for 100% to 50%, then check against 50% to 25%. Two consistent readings confirm the half-life.',
    modelSentence:
      'After three half-lives, the activity is one eighth of the original because it has halved three times.',
  },

  'gcse-p5-5': {
    whyMarksLost: [
      'Using mass instead of weight when calculating force.',
      'Using centimetres instead of metres for extension.',
      'Failing to identify the limit of proportionality.',
    ],
    whatExaminersWant: [
      'F = k e with F in newtons, e in metres and k in N/m.',
      'Recognition of the linear region on a force-extension graph.',
      'Use of EPE = ½ k e² when elastic potential energy is asked for.',
    ],
    commonTraps: [
      'Forgetting weight = mass × gravitational field strength.',
      'Reading total length instead of extension from natural length.',
      'Applying Hooke’s law beyond the limit of proportionality.',
    ],
    gradeNineMove:
      'Before applying Hooke’s law, state "provided the limit of proportionality has not been exceeded".',
    modelSentence:
      'Hooke’s law states that force is proportional to extension, provided the object remains below its limit of proportionality.',
  },

  'gcse-p6-2': {
    whyMarksLost: [
      'Forgetting the order of the electromagnetic spectrum.',
      'Confusing wavelength order with frequency order.',
      'Not stating that all electromagnetic waves travel at the same speed in a vacuum.',
    ],
    whatExaminersWant: [
      'Correct order: radio, microwave, infrared, visible, ultraviolet, X-ray, gamma.',
      'Linkage: higher frequency → higher energy → greater danger.',
      'A specific use and danger for each relevant wave type when asked.',
    ],
    commonTraps: [
      'Confusing ultraviolet with X-rays.',
      'Saying microwaves are sound waves.',
      'Forgetting that electromagnetic waves are transverse waves.',
    ],
    gradeNineMove:
      'Write the spectrum order at the top of your working. It acts as a free reference and prevents most ordering errors.',
    modelSentence:
      'As frequency increases across the electromagnetic spectrum, wavelength decreases and photon energy increases.',
  },

  // ============================================================
  // MATHS GCSE PROBLEMS
  // ============================================================

  'F-001': {
    whyMarksLost: [
      'Rushing the question and missing key words such as "of", "left", "total" or "difference".',
      'Skipping working lines, even when the method is worth marks.',
      'Not stating units in the final answer.',
    ],
    whatExaminersWant: [
      'Clear working for every step.',
      'A final answer that is obvious, preferably underlined or boxed.',
      'Units when the question gives them.',
    ],
    commonTraps: [
      'Mistaking percentage of with percentage off.',
      'Switching numbers around in subtraction.',
      'Answering a different question from the one asked.',
    ],
    gradeNineMove:
      'Re-read the final sentence of the question after calculating. Many marks are lost because students calculate the right thing but answer the wrong thing.',
    modelSentence:
      'The calculation shows the total is 48, but the question asks how many are left, so I must subtract from the starting amount.',
  },

  'H-001': {
    whyMarksLost: [
      'Treating Higher problems as calculator-only questions when algebra is required first.',
      'Not defining variables.',
      'Forgetting that method marks depend on visible working.',
    ],
    whatExaminersWant: [
      'A clear "Let x = ..." line.',
      'A chain of reasoning: equation → rearrangement → substitution → answer.',
      'The final answer interpreted in the context of the question.',
    ],
    commonTraps: [
      'Sign errors when expanding brackets.',
      'Solving for x but forgetting to answer the actual question.',
      'Rounding too early.',
    ],
    gradeNineMove:
      'Once you have an answer, substitute it back into the original situation. This catches most hidden errors.',
    modelSentence:
      'Let x be the original price. The equation models the percentage change, so after solving I must state the price, not just x.',
  },

  // ============================================================
  // ENGLISH GCSE GENERAL
  // ============================================================

  'paper1-language': {
    whyMarksLost: [
      'Quoting too much. Examiners prefer short, embedded quotations.',
      'No method or technique label.',
      'Forgetting writer’s purpose and reader effect.',
    ],
    whatExaminersWant: [
      'A clear analytical structure such as What / How / Why.',
      'Relevant subject terminology.',
      'Effects on the reader linked to the writer’s choices.',
    ],
    commonTraps: [
      'Spotting one technique and stretching it across the whole answer.',
      'Confusing tone with mood.',
      'Writing feature-spotting paragraphs that never analyse meaning.',
    ],
    gradeNineMove:
      'End each analytical paragraph by zooming back out to the writer’s overall purpose.',
    modelSentence:
      'Through the violent verb "crashed", the writer creates a sense of sudden danger, positioning the reader to feel the same shock as the character.',
  },

  // ============================================================
  // ENGLISH LITERATURE — MACBETH
  // ============================================================

  'eng-8-1': {
    whyMarksLost: [
      'Re-telling the plot instead of analysing Shakespeare’s methods.',
      'Forgetting Macbeth is a tragedy.',
      'Missing Jacobean context such as divine right, regicide and James I.',
    ],
    whatExaminersWant: [
      'A clear thesis in the opening sentence.',
      'Embedded quotations integrated into the student’s own argument.',
      'AO3 context tied to specific moments, not bolted on generically.',
    ],
    commonTraps: [
      'Writing about Macbeth as if he is a real person rather than a dramatic construct.',
      'Treating the witches as simple villains rather than ambiguous catalysts.',
      'Ignoring Shakespeare’s structural choices across the tragedy.',
    ],
    gradeNineMove:
      'Use "Shakespeare" more than "Macbeth". Foregrounding the writer immediately strengthens AO2.',
    modelSentence:
      'Shakespeare presents Macbeth as a tragic hero whose fatal ambition corrupts his moral judgement and disrupts the natural order.',
  },

  'eng-8-3': {
    whyMarksLost: [
      'Calling Lady Macbeth simply evil.',
      'Forgetting her trajectory from dominance to psychological collapse.',
      'Overusing "unsex me here" without range.',
    ],
    whatExaminersWant: [
      'A clear character arc.',
      'Three short embedded quotations from different points in the play.',
      'Gender and Jacobean context used carefully.',
    ],
    commonTraps: [
      'Calling Lady Macbeth a feminist in a modern sense.',
      'Forgetting her death happens off-stage.',
      'Ignoring her vulnerability after Duncan’s murder.',
    ],
    gradeNineMove:
      'Argue that her madness becomes Shakespeare’s warning that ambition against the natural order destroys the ambitious.',
    modelSentence:
      'Lady Macbeth initially appears transgressive and dominant, but Shakespeare gradually exposes the psychological cost of rejecting natural and moral boundaries.',
  },

  'eng-8-13': {
    whyMarksLost: [
      'Listing themes without linking them.',
      'Treating fate versus free will as a simple binary.',
      'Missing how imagery develops themes.',
    ],
    whatExaminersWant: [
      'Theme, imagery and quotation in every paragraph.',
      'Clear explanation of Shakespeare’s purpose.',
      'Cross-references between guilt, ambition, kingship, violence and the supernatural.',
    ],
    commonTraps: [
      'Pinning ambition only on Macbeth.',
      'Forgetting the political context of kingship.',
      'Writing context paragraphs that are not linked to the question.',
    ],
    gradeNineMove:
      'Use the phrase "Shakespeare interrogates" instead of "Shakespeare shows" when making a sophisticated thematic argument.',
    modelSentence:
      'Shakespeare interrogates ambition by presenting it as both psychologically seductive and morally destructive.',
  },

  // ============================================================
  // ENGLISH LITERATURE — A CHRISTMAS CAROL
  // ============================================================

  'eng-9-1': {
    whyMarksLost: [
      'Writing about redemption without showing the stave-by-stave transformation.',
      'Ignoring Dickens’s social purpose.',
      'Forgetting the novella form and its compressed moral structure.',
    ],
    whatExaminersWant: [
      'Stave-anchored evidence.',
      'Dickens’s social criticism linked to Victorian poverty and responsibility.',
      'Quotations that show change.',
    ],
    commonTraps: [
      'Calling Scrooge simply happy at the end rather than redeemed.',
      'Forgetting Marley’s chains are an extended metaphor.',
      'Using vague context such as "Victorians were poor" without precision.',
    ],
    gradeNineMove:
      'Link Dickens’s social purpose to a specific Victorian context, such as the Poor Law, Malthusian attitudes or industrial inequality.',
    modelSentence:
      'Dickens uses Scrooge’s transformation to challenge wealthy Victorian readers who ignored poverty while claiming moral respectability.',
  },

  'eng-9-3': {
    whyMarksLost: [
      'Treating Scrooge as static.',
      'Skipping the language of the opening description.',
      'Forgetting Fred as a foil.',
    ],
    whatExaminersWant: [
      'A clear arc: cold misanthrope → fearful penitent → joyous benefactor.',
      'Analysis of Dickens’s language choices.',
      'Comparison to other characters who highlight Scrooge’s values.',
    ],
    commonTraps: [
      'Saying Scrooge becomes "nice".',
      'Forgetting the narrator’s irony.',
      'Ignoring the structural role of the ghosts.',
    ],
    gradeNineMove:
      'Use "I will live in the Past, the Present, and the Future" as a structural quote that captures the novella’s moral architecture.',
    modelSentence:
      'Scrooge’s movement from isolation to generosity allows Dickens to dramatise the possibility of moral rebirth.',
  },

  // ============================================================
  // ENGLISH LITERATURE — AN INSPECTOR CALLS
  // ============================================================

  'eng-10-1': {
    whyMarksLost: [
      'Forgetting the dual context of 1912 and 1945.',
      'Treating the Inspector as only a literal policeman.',
      'Ignoring Priestley’s socialist message.',
    ],
    whatExaminersWant: [
      'Dramatic irony linked to 1912 events the audience knows will happen.',
      'Recognition of Priestley’s polemic purpose.',
      'Stage directions analysed as carefully as dialogue.',
    ],
    commonTraps: [
      'Confusing Eric and Gerald.',
      'Calling the Inspector supernatural without considering multiple interpretations.',
      'Forgetting the play is deliberately constructed, not naturalistic reality.',
    ],
    gradeNineMove:
      'In every paragraph, separate the 1912 setting from the 1945 audience. That dual lens is a top-band habit.',
    modelSentence:
      'Priestley uses the Inspector as a dramatic device to expose the moral failures of capitalist individualism.',
  },

  'eng-10-2': {
    whyMarksLost: [
      'Calling Birling wrong without explaining how Priestley undermines him.',
      'Forgetting props such as cigars, champagne and the dinner table.',
      'Treating Sheila as merely naive.',
    ],
    whatExaminersWant: [
      'Specific dramatic irony references.',
      'The Birlings understood as a group portrait of privilege.',
      'Sheila and Eric recognised as Priestley’s hope for generational change.',
    ],
    commonTraps: [
      'Reading Birling’s individualism as just arrogance rather than ideology.',
      'Missing Mrs Birling’s refusal to change.',
      'Ignoring stagecraft.',
    ],
    gradeNineMove:
      'Argue that Priestley uses the Birling family as a microcosm of pre-war capitalist society.',
    modelSentence:
      'The Birling family functions as a microcosm of Edwardian privilege, allowing Priestley to criticise the selfishness of capitalist society.',
  },

  // ============================================================
  // POWER AND CONFLICT POETRY
  // ============================================================

  'eng-11-1': {
    whyMarksLost: [
      'Forgetting the comparison must be critical.',
      'Spending too long on the named poem.',
      'Choosing a comparison poem that shares only a surface-level theme.',
    ],
    whatExaminersWant: [
      'A balanced comparison.',
      'Comparison of form, structure and language.',
      'A thesis that introduces both poems and the angle of comparison.',
    ],
    commonTraps: [
      'Writing two separate mini-essays instead of interwoven comparison.',
      'Forgetting context.',
      'Comparing content only.',
    ],
    gradeNineMove:
      'Use a comparative phrase in every topic sentence so the comparison is visible throughout.',
    modelSentence:
      'Both poets explore the instability of power, but Shelley presents human authority as temporary, whereas Owen focuses on the psychological damage caused by military power.',
  },

  'eng-11-2': {
    whyMarksLost: [
      'Skipping the sonnet form.',
      'Missing the traveller frame.',
      'Calling Ozymandias simply evil.',
    ],
    whatExaminersWant: [
      'Analysis of the disrupted sonnet form.',
      'The irony of the inscription.',
      'Romantic context and Shelley’s scepticism about tyranny.',
    ],
    commonTraps: [
      'Calling the desert only a metaphor.',
      'Treating "shattered visage" as simple description.',
      'Ignoring the volta or irregular rhyme scheme.',
    ],
    gradeNineMove:
      'Argue that Shelley’s irregular rhyme scheme enacts the fragmentation of Ozymandias’s power.',
    modelSentence:
      'Shelley destabilises the sonnet form to mirror the collapse of Ozymandias’s authority and the fragmentation of human power over time.',
  },

  // ============================================================
  // UNSEEN POETRY
  // ============================================================

  'eng-12-1': {
    whyMarksLost: [
      'Spending too long trying to understand every line.',
      'Inventing context that is not available.',
      'Focusing on one technique and ignoring the rest of the poem.',
    ],
    whatExaminersWant: [
      'A quick plan based on tone, imagery, form and structure.',
      'At least three short embedded quotations.',
      'A confident opening judgement about tone or meaning.',
    ],
    commonTraps: [
      'Naming rhyme schemes without analysing effect.',
      'Saying the poem is "deep".',
      'Forgetting to track changes across the poem.',
    ],
    gradeNineMove:
      'Open with a precise tone word such as elegiac, defiant, nostalgic, bitter, sardonic or intimate, then prove it.',
    modelSentence:
      'The poem opens with an elegiac tone, using natural imagery to present memory as fragile but emotionally powerful.',
  },

  // ============================================================
  // LANGUAGE PAPER 1
  // ============================================================

  'eng-13-3': {
    whyMarksLost: [
      'Writing about language instead of structure.',
      'Treating structure as only paragraph order.',
      'Using long quotations instead of references.',
    ],
    whatExaminersWant: [
      'Tracking the movement of focus through the extract.',
      'Structural terminology such as shift, focus, withholding, foreshadowing, contrast and cyclical structure.',
      'Clear explanation of how the reader is positioned.',
    ],
    commonTraps: [
      'Confusing structure with form.',
      'Listing structural features without explaining effect.',
      'Ignoring the beginning and ending.',
    ],
    gradeNineMove:
      'Use structural verbs such as shifts, withholds, foreshadows, narrows, widens or disrupts.',
    modelSentence:
      'The writer initially narrows the focus onto the isolated character before widening the perspective to reveal the threatening setting.',
  },

  'eng-13-4': {
    whyMarksLost: [
      'Treating Q4 as a language analysis question only.',
      'Agreeing with the statement too simply.',
      'Writing too many shallow paragraphs.',
    ],
    whatExaminersWant: [
      'Sustained engagement with the statement.',
      'Critical evaluation of methods and effects.',
      'A confident evaluative voice.',
    ],
    commonTraps: [
      'Analysing language without linking back to the statement.',
      'Disagreeing just to seem sophisticated.',
      'Forgetting to evaluate how successfully the writer achieves an effect.',
    ],
    gradeNineMove:
      'Frame your final paragraph around "Ultimately, the writer succeeds because...".',
    modelSentence:
      'Ultimately, the writer succeeds because the violent imagery and narrowing focus make the character’s fear feel both immediate and convincing.',
  },

  'eng-13-6': {
    whyMarksLost: [
      'Spending too long planning.',
      'Listing details instead of building a sustained mood.',
      'Forgetting sentence variation.',
    ],
    whatExaminersWant: [
      'A single moment described in precise sensory detail.',
      'A central image or metaphor sustained across the piece.',
      'A clear structural arc: hook, development, final image.',
    ],
    commonTraps: [
      'Writing a full story when asked for description.',
      'Overusing adjectives.',
      'Forgetting paragraph control.',
    ],
    gradeNineMove:
      'Plan one central image and weave it through the whole response.',
    modelSentence:
      'The sea crouched beneath the cliff like a hungry animal, dragging stones backwards with every breath.',
  },

  // ============================================================
  // LANGUAGE PAPER 2
  // ============================================================

  'eng-14-3': {
    whyMarksLost: [
      'Quoting both sources without synthesising them.',
      'Forgetting Q2 is a summary question, not language analysis.',
      'Making vague comparisons.',
    ],
    whatExaminersWant: [
      'A comparative thesis.',
      'Inference rather than simple retrieval.',
      'Linking phrases throughout.',
    ],
    commonTraps: [
      'Treating the sources separately.',
      'Quoting too much and inferring too little.',
      'Ignoring differences in perspective.',
    ],
    gradeNineMove:
      'Open every paragraph with a comparative connective rather than "Source A says...".',
    modelSentence:
      'Both writers present travel as challenging, but Source A emphasises physical danger whereas Source B focuses on emotional discomfort.',
  },

  'eng-14-7': {
    whyMarksLost: [
      'Forgetting the required form.',
      'Using the wrong register.',
      'Skipping rhetorical devices.',
    ],
    whatExaminersWant: [
      'Clear form conventions.',
      'Register matched to purpose and audience.',
      'Rhetorical devices used deliberately, not randomly.',
    ],
    commonTraps: [
      'Writing a speech that reads like an essay.',
      'Forgetting paragraphing.',
      'Using statistics that sound invented or implausible.',
    ],
    gradeNineMove:
      'Open with a striking anecdote or statistic and close with a call to action.',
    modelSentence:
      'So, the question is not whether we can afford to act; it is whether we can afford to keep pretending the problem will solve itself.',
  },

  // ============================================================
  // MATHS GCSE TOPICS — ALGEBRA
  // ============================================================

  '2.4': {
    whyMarksLost: [
      'Taking out an incomplete common factor.',
      'Dropping a minus sign when the leading coefficient is negative.',
      'Forgetting to check by re-expanding.',
    ],
    whatExaminersWant: [
      'The highest common factor taken out.',
      'Both numerical and algebraic factors included.',
      'A check line by expanding back.',
    ],
    commonTraps: [
      'Confusing factorise with simplify.',
      'Writing 5x + 10 = 5(x + 10).',
      'Missing the x from the common factor.',
    ],
    gradeNineMove:
      'After every factorisation, expand back mentally or in writing to check it returns to the original expression.',
    modelSentence:
      '6x² + 9x = 3x(2x + 3), because both terms share a factor of 3x.',
  },

  '2.5': {
    whyMarksLost: [
      'Doing an operation to one side but not the other.',
      'Sign errors when rearranging.',
      'Dividing by a negative without flipping the inequality.',
    ],
    whatExaminersWant: [
      'Every line of working shown.',
      'A clear final line.',
      'Substitution back into the original equation where useful.',
    ],
    commonTraps: [
      'Forgetting to divide every term.',
      'Expanding brackets incorrectly.',
      'Changing signs without showing the operation.',
    ],
    gradeNineMove:
      'Treat the equals sign like a balance. Whatever happens to one side must happen to the other.',
    modelSentence:
      '3x + 5 = 20, so 3x = 15 and x = 5.',
  },

  '2.9': {
    whyMarksLost: [
      'Using the quadratic formula when simple factorising would work.',
      'Forgetting that quadratics usually have two solutions.',
      'Sign errors in factor pairs.',
    ],
    whatExaminersWant: [
      'A factor pair that multiplies to c and adds to b.',
      'Both solutions clearly stated.',
      'A final line using "or".',
    ],
    commonTraps: [
      'Stopping after one root.',
      'Mixing signs in the brackets.',
      'Forgetting to set each bracket equal to zero.',
    ],
    gradeNineMove:
      'Write "x = a or x = b". The word "or" makes it clear you understand there are two possible solutions.',
    modelSentence:
      'x² − 5x + 6 = 0 factorises to (x − 2)(x − 3) = 0, so x = 2 or x = 3.',
  },

  '2.10': {
    whyMarksLost: [
      'Misquoting the formula.',
      'Sign errors inside the discriminant.',
      'Forgetting both roots.',
    ],
    whatExaminersWant: [
      'The formula written correctly.',
      'The discriminant calculated separately.',
      'Both roots rounded as requested.',
    ],
    commonTraps: [
      'Writing -b² instead of (-b)².',
      'Dropping the ± symbol.',
      'Rounding too early.',
    ],
    gradeNineMove:
      'State the discriminant b² − 4ac on its own line. It helps decide the number and type of roots.',
    modelSentence:
      'The discriminant is b² − 4ac, so I calculate this first before substituting into the quadratic formula.',
  },

  '2.11': {
    whyMarksLost: [
      'Forgetting to balance the constant.',
      'Sign errors when the middle coefficient is negative.',
      'Confusing completing the square with solving.',
    ],
    whatExaminersWant: [
      'Half the coefficient of x inside the bracket.',
      'Subtracting or adding the square of that number outside the bracket.',
      'A final form of (x + p)² + q.',
    ],
    commonTraps: [
      'Writing x² + 6x = (x + 3)².',
      'Forgetting the outside correction.',
      'Not factoring out the coefficient when the x² coefficient is not 1.',
    ],
    gradeNineMove:
      'Link completing the square to the turning point of a quadratic graph.',
    modelSentence:
      'x² + 6x = (x + 3)² − 9 because expanding (x + 3)² creates an extra 9.',
  },

  // ============================================================
  // MATHS GCSE TOPICS — GRAPHS
  // ============================================================

  '3.5': {
    whyMarksLost: [
      'Confusing gradient with y-intercept.',
      'Reading coordinates incorrectly.',
      'Forgetting rules for parallel and perpendicular lines.',
    ],
    whatExaminersWant: [
      'y = mx + c with m and c identified.',
      'A clear gradient calculation.',
      'Final equation simplified.',
    ],
    commonTraps: [
      'Dividing the coordinate differences the wrong way round.',
      'Saying y = 3x + 2 has gradient 2.',
      'Using the wrong sign for a negative gradient.',
    ],
    gradeNineMove:
      'For perpendicular lines, write the original gradient, then the negative reciprocal, then the final equation.',
    modelSentence:
      'For y = 3x + 2, the gradient is 3 and the y-intercept is 2.',
  },

  // ============================================================
  // MATHS GCSE TOPICS — GEOMETRY
  // ============================================================

  '5.7': {
    whyMarksLost: [
      'Mixing up sin, cos and tan.',
      'Forgetting to set the calculator to degrees.',
      'Using the wrong side as opposite or adjacent.',
    ],
    whatExaminersWant: [
      'A labelled diagram.',
      'The chosen ratio stated clearly.',
      'Final answer rounded correctly.',
    ],
    commonTraps: [
      'Using opposite and adjacent relative to the wrong angle.',
      'Rounding too early.',
      'Using sine rule or cosine rule when right-angle trigonometry is enough.',
    ],
    gradeNineMove:
      'Write SOHCAHTOA, then circle the side pair you have. This reduces ratio-selection mistakes.',
    modelSentence:
      'Because I know the opposite and adjacent sides, I use tan θ = opposite ÷ adjacent.',
  },

  // ============================================================
  // MATHS GCSE TOPICS — PROBABILITY
  // ============================================================

  '6.7': {
    whyMarksLost: [
      'Forgetting probabilities on branches must sum to 1.',
      'Multiplying when they should add, or adding when they should multiply.',
      'Confusing independent and dependent events.',
    ],
    whatExaminersWant: [
      'A clearly labelled tree diagram.',
      'Multiplication along branches.',
      'Addition across alternative branches.',
    ],
    commonTraps: [
      'Forgetting "without replacement" changes the second probability.',
      'Adding probabilities along one branch.',
      'Leaving fractions unsimplified when asked to simplify.',
    ],
    gradeNineMove:
      'Check every split in the tree: the probabilities from each point should add to 1.',
    modelSentence:
      'For this path I multiply the branch probabilities, then I add it to the probability of the other successful path.',
  },

  // ============================================================
  // MATHS GCSE TOPICS — NUMBER / PROPORTION
  // ============================================================

  '4.4': {
    whyMarksLost: [
      'Confusing percentage of with percentage increase or decrease.',
      'Handling reverse percentages incorrectly.',
      'Forgetting compound interest uses repeated multiplication.',
    ],
    whatExaminersWant: [
      'A clear multiplier.',
      'For compound interest, the multiplier raised to the number of years.',
      'Money answers rounded to two decimal places where appropriate.',
    ],
    commonTraps: [
      'Thinking a 20% decrease followed by a 20% increase returns to the original value.',
      'Subtracting in reverse percentage questions instead of dividing by the multiplier.',
      'Using simple interest when the question asks for compound interest.',
    ],
    gradeNineMove:
      'Write the multiplier explicitly before calculating. It almost always secures a method mark.',
    modelSentence:
      'A 15% decrease uses the multiplier 0.85, so the new value is original × 0.85.',
  },
};

/**
 * Internal set used for development diagnostics.
 *
 * Each time a bank key is successfully attached to a topic/problem, the id is stored here.
 * After rendering, the dev warning can identify orphaned keys that do not match any loaded content.
 */
const _MATCHED_BANK_KEYS = new Set();

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const markMatched = (id) => {
  if (id) _MATCHED_BANK_KEYS.add(String(id));
};

const getBankEntry = (id) => {
  if (id === undefined || id === null) return null;
  return EXAMINER_BANK[String(id)] || null;
};

/**
 * Attaches an examiner insight block to a single topic/problem/item.
 *
 * Supports nested structures:
 * - topic.topics
 * - topic.children
 * - topic.lessons
 * - topic.problems
 * - topic.questions
 * - topic.items
 *
 * The function is pure and does not mutate the original object.
 */
const attachToItem = (item) => {
  if (!isObject(item)) return item;

  const bank = getBankEntry(item.id);

  const nextItem = {
    ...item,
  };

  if (bank) {
    markMatched(item.id);

    // Preserve explicitly authored topic-level examinerInsights if already present.
    nextItem.examinerInsights = item.examinerInsights || bank;
  }

  if (Array.isArray(item.topics)) {
    nextItem.topics = attachExaminerInsights(item.topics);
  }

  if (Array.isArray(item.children)) {
    nextItem.children = attachExaminerInsights(item.children);
  }

  if (Array.isArray(item.lessons)) {
    nextItem.lessons = attachExaminerInsights(item.lessons);
  }

  if (Array.isArray(item.problems)) {
    nextItem.problems = attachExaminerInsights(item.problems);
  }

  if (Array.isArray(item.questions)) {
    nextItem.questions = attachExaminerInsights(item.questions);
  }

  if (Array.isArray(item.items)) {
    nextItem.items = attachExaminerInsights(item.items);
  }

  return nextItem;
};

/**
 * Main decorator helper.
 *
 * Usage:
 *   topics: attachExaminerInsights(section.topics)
 *
 * Or:
 *   const decoratedProblems = attachExaminerInsights(problemSet)
 */
export function attachExaminerInsights(items) {
  if (!Array.isArray(items)) return [];
  return items.map(attachToItem);
}

/**
 * Decorates a full section object.
 *
 * Useful where a subject file exports sections like:
 * {
 *   id,
 *   title,
 *   topics: [...]
 * }
 */
export function attachExaminerInsightsToSection(section) {
  if (!isObject(section)) return section;

  return {
    ...section,
    topics: Array.isArray(section.topics)
      ? attachExaminerInsights(section.topics)
      : section.topics,
    children: Array.isArray(section.children)
      ? attachExaminerInsights(section.children)
      : section.children,
  };
}

/**
 * Decorates an array of sections.
 *
 * Useful for English/Science files that export:
 * export const englishGCSESections = [...]
 */
export function attachExaminerInsightsToSections(sections) {
  if (!Array.isArray(sections)) return [];
  return sections.map(attachExaminerInsightsToSection);
}

/**
 * Decorates a single topic/problem/question object.
 *
 * Useful when a Maths problem opens directly and does not pass through a topic list.
 */
export function attachExaminerInsightToItem(item) {
  return attachToItem(item);
}

/**
 * Manual lookup helper.
 */
export function getExaminerInsight(id) {
  return getBankEntry(id);
}

/**
 * Boolean helper for render logic.
 */
export function hasExaminerInsight(id) {
  return Boolean(getBankEntry(id));
}

/**
 * Development helper.
 *
 * Returns any bank keys that have not yet matched a topic/problem/question during runtime.
 */
export function getUnmatchedExaminerBankKeys() {
  return Object.keys(EXAMINER_BANK).filter((key) => !_MATCHED_BANK_KEYS.has(key));
}

/**
 * Environment-safe dev detection.
 *
 * Works with:
 * - Vite: import.meta.env.MODE
 * - CRA / Node-like builds: process.env.NODE_ENV
 */
const isDevelopment = (() => {
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      typeof process.env.NODE_ENV === 'string'
    ) {
      return process.env.NODE_ENV !== 'production';
    }
  } catch {
    // Ignore environment detection errors.
  }

  return false;
})();

/**
 * Development-only diagnostics.
 *
 * Warns when a bank entry has not matched any loaded topic/problem id.
 * This catches:
 * - mistyped ids
 * - deleted topics
 * - renamed problems
 * - content not being passed through the decorator
 */
if (typeof window !== 'undefined' && isDevelopment) {
  window.setTimeout(() => {
    const orphans = getUnmatchedExaminerBankKeys();

    if (orphans.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        '[examinerBank] Bank keys with no matching topic/problem/question id:',
        orphans
      );
    }
  }, 2500);
}